// cleanup_convert_to_typescript_file_BTR

/* istanbul ignore file */
import axios from 'axios';
import { memoize } from 'lodash';
import moment from 'moment-business-days';
import { from } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { memoizeWithToken, setTokenAndParamsAsCacheKey } from 'api/utils/memoizeUtils';
import { API_DATE_FORMAT, MOMENT_CALENDAR_LOCALES } from 'constants.js';

const IS_BTR_SETTINGS_ENABLED = {
  ONE: '1',
  TRUE: 'true'
};

export function convertToIndexes(array) {
  return array.map((day, index) => day && index).filter((day) => day !== false);
}

/**
 * /api/balance-transaction/calendar
 */
export function calendar() {
  return from(axios.post('/balance-transaction/calendar')).pipe(
    map(({ data: { data } }) => data),
    tap(({ businessDaysOfWeek, holidays }) => {
      moment.defineLocale(MOMENT_CALENDAR_LOCALES.EN_BALANCE_TRANSACTION, {
        holidays,
        parentLocale: 'en',
        workingWeekdays: convertToIndexes(businessDaysOfWeek)
      });
    })
  );
}

export const getAccounts = (accountCategory) => () => {
  const postData = {
    accountCategory: `${accountCategory.toUpperCase()}_ACCOUNTS`
  };
  return from(axios.post('/balance-transaction/balancetransactions/getAccounts', postData)).pipe(
    map(({ data: { data } }) => data)
  );
};

export const getWireAccounts$ = (accountCategory) => () => {
  const postData = {
    accountCategory: `${accountCategory.toUpperCase()}_ACCOUNTS`
  };
  return from(
    axios.post('/balance-transaction/balancetransactions/getWireAccounts', postData)
  ).pipe(map(({ data: { data, success } }) => ({ data, success })));
};

/**
 * /api/balance-transaction/balancetransactions/getDepositAccountDetails
 *
 * @param {object} postData
 * @param {string} postData.accountCategory - Account category
 * @param {number[]} postData.accountIdList - Account id's
 * @param {moment} postData.endDate - end date
 * @param {moment} postData.startDate - start date
 * @return @TODO
 */
export function getAccountDetails({ accountCategory, accountIdList, endDate, startDate }) {
  const postData = {
    accountCategory: `${accountCategory.toUpperCase()}_ACCOUNTS`,
    accountIdList,
    endDate: moment(endDate).format(API_DATE_FORMAT),
    startDate: moment(startDate).format(API_DATE_FORMAT)
  };

  return from(
    axios.post('/balance-transaction/balancetransactions/getAccountDetails', postData)
  ).pipe(map(({ data: { data, result } }) => ({ data, result })));
}

export const getFinancialAccountSummary =
  (accountCategory, additionalData) =>
  ({ accountIdList, balancesFor = 'SUMMARY', endDate, startDate } = {}) => {
    const postData = {
      accountCategory: `${accountCategory.toUpperCase()}_ACCOUNTS`,
      accountIdList,
      balancesFor,
      endDate: moment(endDate).format(API_DATE_FORMAT),
      startDate: moment(startDate).format(API_DATE_FORMAT),
      ...additionalData
    };

    return from(
      axios.post('/balance-transaction/financialoverview/getAccountSummary', postData)
    ).pipe(map(({ data, ...rest }) => ({ ...data, ...rest })));
  };

export const getFinancialAccountsCount = (postData) =>
  from(axios.post('/balance-transaction/financialoverview/getAccountsCount', postData)).pipe(
    map(({ data: { data: { accountsCount } = {} } = {} }) => accountsCount)
  );

export const getAccountSummary =
  (accountCategory, additionalData) =>
  ({ accountIdList, balancesFor = 'SUMMARY', endDate, startDate } = {}) => {
    const postData = {
      accountCategory: `${accountCategory.toUpperCase()}_ACCOUNTS`,
      accountIdList,
      balancesFor,
      endDate: moment(endDate).format(API_DATE_FORMAT),
      startDate: moment(startDate).format(API_DATE_FORMAT),
      ...additionalData
    };

    return from(
      axios.post('/balance-transaction/balancetransactions/getAccountSummary', postData)
    ).pipe(map(({ data, ...rest }) => ({ ...data, ...rest })));
  };

export const getPermissions = memoizeWithToken(() =>
  from(axios.post('/balance-transaction/balancetransactions/getPermissions')).pipe(
    map(({ data: { data } }) => data),
    map((permissions) =>
      Object.keys(permissions).reduce(
        (acc, type) => ({
          ...acc,
          [type.replace('_ACCOUNTS', '').toLowerCase()]: permissions[type]
        }),
        {}
      )
    )
  )
);

export const getBTRTransactionTypes = memoize(
  (accountType, sameDayTransactions = false) =>
    from(
      axios.post('/balance-transaction/balancetransactions/getBTRTransactionTypes', {
        accountType: `${accountType.toUpperCase()}_ACCOUNTS`,
        sameDayTransactions
      })
    ).pipe(map(({ data: { data } }) => data)),
  (accountType, sameDayTransactions) =>
    setTokenAndParamsAsCacheKey({ accountType, sameDayTransactions })
);

/*
 * API always returns 'systemOptionsMap as an empty object
 */
export const getSystemConfig = memoizeWithToken(() =>
  from(axios.post('/balance-transaction/balancetransactions/getSystemConfig')).pipe(
    map(({ data: { data } }) => data.systemOptionsMap)
  )
);

/**
 * /api/balance-transaction/balancetransactions/getTransactionSearchDetails
 *
 * @param {object} postData
 * @param {integer[]} postData.accountIdList Contains the account id
 * @param {Moment} postData.endDate End Date
 * @param {Moment} postData.startDate Start Date
 * @param {integer[]} [postData.baiTypes] BAI Types
 * @param {Number} [postData.maxAmount] Max Amount - expected in decimal format
 * @param {Number} [postData.minAmount] Min Amount - expected in decimal format
 * @param {Number} [postData.serialFrom] Check number From - expected in integer format
 * @param {Number} [postData.serialTo] Check number To - expected in integer format
 * @param {string[]} [postData.transactionTypes] Transaction Type List
 * @returns {function} a function to invoke that makes the API call
 */
export const getTransactionSearchDetails =
  ({
    accountCategory,
    accountIdList,
    baiTypes,
    datePickerChosenLabel,
    endDate,
    isFromWidget = false,
    maxAmount,
    minAmount,
    serialFrom,
    serialTo,
    singleTransaction,
    startDate,
    transactionType,
    transactionTypes,
    unmaskAccount
  }) =>
  () => {
    const isSameDay = moment().isSame(startDate, 'day') || moment().isSame(endDate, 'day');
    const postData = {
      accountCategory: `${accountCategory.toUpperCase()}_ACCOUNTS` || null,
      accountIdList,
      baiTypes,
      dateRange: datePickerChosenLabel,
      endDate: moment(endDate).format(API_DATE_FORMAT),
      maxAmount,
      minAmount,
      serialFrom,
      serialTo,
      startDate: moment(startDate).format(API_DATE_FORMAT),
      transactionTypes: transactionType ? [transactionType] : [],
      unmaskAccount,
      includeSameDayTransactions: isFromWidget ? false : isSameDay
    };
    if (singleTransaction) {
      Object.assign(postData.transactionTypes, [singleTransaction.code]);
    }
    if (transactionTypes) {
      if (transactionTypes[0] === '-1') {
        Object.assign(postData.transactionTypes, []);
      } else {
        Object.assign(postData.transactionTypes, [transactionTypes[0]]);
      }
    }
    return from(
      axios.post('/balance-transaction/balancetransactions/getTransactionSearchDetails', postData)
    ).pipe(map(({ data }) => data));
  };

export const getSettings = memoizeWithToken(() =>
  from(axios.post('/balance-transaction/balancetransactions/getSettings')).pipe(
    map(({ data: { data } }) => data),
    map(
      ({
        BR_ENABLE_PREMIER_ACTIVITY_REPORTING,
        BR_ENABLE_PREMIER_BALANCE_HISTORY,
        BR_ENABLE_RUNNING_BALANCE,
        BR_MAX_ACCOUNT_FOR_REPORTING, // Common param used by both Custom Reporting and Account Activity to set the max limits
        BR_MAX_DATE,
        BR_MAX_DATE_RANGE,
        BR_REALTIME_CURRENT_DAY_TRANSACTION_STATE,
        BR_SHOW_BANKREF,
        BR_SHOW_CUSTREF,
        BR_SORT_ACCOUNT_ALPHA,
        ENABLE_ACCOUNT_MASKING
      }) => ({
        minDate: moment(BR_MAX_DATE),
        maxAccountsForReporting: BR_MAX_ACCOUNT_FOR_REPORTING,
        maxDateRange: BR_MAX_DATE_RANGE,
        showActivityReport: [IS_BTR_SETTINGS_ENABLED.ONE, IS_BTR_SETTINGS_ENABLED.TRUE].includes(
          BR_ENABLE_PREMIER_ACTIVITY_REPORTING
        ),
        showCustomReporting: [IS_BTR_SETTINGS_ENABLED.ONE, IS_BTR_SETTINGS_ENABLED.TRUE].includes(
          BR_ENABLE_PREMIER_BALANCE_HISTORY
        ),
        showBankReference: BR_SHOW_BANKREF === IS_BTR_SETTINGS_ENABLED.ONE,
        showCustomerReference: BR_SHOW_CUSTREF === IS_BTR_SETTINGS_ENABLED.ONE,
        showRunningTotals: [IS_BTR_SETTINGS_ENABLED.ONE, IS_BTR_SETTINGS_ENABLED.TRUE].includes(
          BR_ENABLE_RUNNING_BALANCE
        ),
        maskAccount: ENABLE_ACCOUNT_MASKING,
        sortAccountAlpha: Number(BR_SORT_ACCOUNT_ALPHA),
        realtimeCurrentDayTransactionState: BR_REALTIME_CURRENT_DAY_TRANSACTION_STATE
      })
    )
  )
);

export const isSummaryTotalsAvailable = () => {
  return from(
    axios.post('/balance-transaction/balancetransactions/isSummaryTotalsAvailable')
  ).pipe(map(({ data: { data } }) => ({ data })));
};

export function getPermittedAccountTypes() {
  return from(
    axios.post('/balance-transaction/balancetransactions/getPermittedAccountTypes')
  ).pipe(
    map(({ data: { data } }) => data),
    map(
      ({
        DEPOSIT_ACCOUNTS,
        INCOMING_WIRES,
        INVESTMENT_ACCOUNTS,
        LOAN_ACCOUNTS,
        OTHER_ACCOUNTS
      }) => ({
        deposit: DEPOSIT_ACCOUNTS,
        wires: INCOMING_WIRES,
        investment: INVESTMENT_ACCOUNTS,
        loan: LOAN_ACCOUNTS,
        other: OTHER_ACCOUNTS
      })
    )
  );
}

// Advanced Report

export function getAdvancedReportingAccounts() {
  return from(axios.post('/balance-transaction/advancedreport/getPermittedAccounts')).pipe(
    map(({ data: { data } }) => data)
  );
}

export function previousBusinessDay() {
  return from(axios.post('/balance-transaction/advancedreport/previousBusinessDay')).pipe(
    map(({ data: { data } }) => data)
  );
}

export function getCustomReport(postData, config) {
  return from(
    axios.post('/balance-transaction/advancedreport/getCustomReport', postData, config)
  ).pipe(map(({ data: { data } }) => data));
}

export function getCustomReportList() {
  return from(axios.post('/balance-transaction/advancedreport/getCustomReportCriteriaList')).pipe(
    map(({ data: { data } }) =>
      data
        .map((report) => {
          try {
            const criteria = JSON.parse(report.criteria);
            return { ...report, criteria };
          } catch (e) {
            return null;
          }
        })
        .filter(Boolean)
    )
  );
}

export function saveCustomReport({ criteria, reportShared }) {
  return from(
    axios.post('/balance-transaction/advancedreport/saveCustomReportCriteria', {
      criteria: JSON.stringify(criteria),
      reportShared
    })
  ).pipe(map(({ data: { data, success } }) => ({ data, success })));
}

export function updateCustomReport(postData) {
  return from(
    axios.post('/balance-transaction/advancedreport/updateCustomReportSharedCriteria', postData)
  ).pipe(map(({ data: { data, success } }) => ({ data, success })));
}

export function deleteCustomReport(postData) {
  return from(
    axios.post('/balance-transaction/advancedreport/deleteCustomReportCriteria', postData)
  ).pipe(map(({ data: { data, success } }) => ({ data, success })));
}

export function exportReport(postData, config) {
  return from(axios.post('/balance-transaction/advancedreport/export', postData, config)).pipe(
    map(({ data: { data } }) => data)
  );
}

export function logCustomReportPrint(event) {
  return from(
    axios.post('/balance-transaction/advancedreport/auditCustomReportPrint', event)
  ).pipe(
    map(({ data }) => ({
      ...data,
      data: data.success ? data.data : {}
    }))
  );
}

// Account Summary Tiles
export const getTiles = memoizeWithToken(() => {
  return from(axios.post('/balance-transaction/balancetransactions/getAllAccountsSummary')).pipe(
    map(({ data: { data } }) => data)
  );
});

// Account Summary Tiles
export function getFinancialOverviewTiles() {
  return from(axios.post('/balance-transaction/financialoverview/getAllAccountsSummary')).pipe(
    map(({ data: { data } }) => data)
  );
}

// Account Activity
export function getAccountActivity(payload) {
  return from(axios.post('/balance-transaction/transactionsearch/getList', payload)).pipe(
    map(({ data: { data } }) => data)
  );
}

// Save Report
export function saveReport(payload) {
  return from(axios.post('/balance-transaction/transactionsearch/save', payload)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

// Delete Report
export function deleteReporting(payload) {
  return from(axios.post('/balance-transaction/transactionsearch/delete', payload)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

// Get Visualization
export function getVisualization(payload) {
  return from(
    axios.post('/balance-transaction/balancetransactions/getVisualization', payload)
  ).pipe(map(({ data: { data } }) => data));
}

// Get Max Date
export function getMaxDays() {
  return from(axios.post('/infrastructure/calendar/getBrMaxDateRange')).pipe(
    map(({ data: { data } }) => data)
  );
}

export function getIncomingWireDetail(payload) {
  return from(
    axios.post('/balance-transaction/balancetransactions/getIncomingWireDetail', payload)
  ).pipe(map(({ data: { data, success } }) => ({ data, success })));
}
